import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  AccountTree as WorkflowsIcon,
  History as ExecutionsIcon,
  Person as ProfileIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  CloudDownload as CloudDownloadIcon,
  ChatBubbleOutline as ChatIcon,
  Edit as NodeEditorIcon,
  AdminPanelSettings as AdminIcon,
  ChevronLeft as ChevronLeftIcon,
  Logout as LogoutIcon,
  CreditCard as SubscriptionIcon,
  Hub as RegistryIcon,
} from '@mui/icons-material';

import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 240;

const userMenuItems = [
  { text: 'Profile', icon: <ProfileIcon />, path: '/app/profile' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/app/settings' },
];

const adminMenuItems = [
  { text: 'Admin Dashboard', icon: <AdminIcon />, path: '/admin/dashboard', perms: ['org.manage'] },
  { text: 'Tenants', icon: <DashboardIcon />, path: '/admin/tenants', perms: ['org.manage'] },
  { text: 'Users', icon: <PersonIcon />, path: '/admin/users', perms: ['org.manage', 'user.manage'] },
  { text: 'Permissions', icon: <SettingsIcon />, path: '/admin/permissions', perms: ['org.manage', 'user.manage'] },
  { text: 'Analytics', icon: <DashboardIcon />, path: '/admin/analytics', perms: ['org.manage'] },
  { text: 'Credentials', icon: <SettingsIcon />, path: '/admin/credentials', perms: ['credential.view'] },
  { text: 'Subscriptions', icon: <SubscriptionIcon />, path: '/admin/subscriptions', perms: ['org.manage'] },
];

const appMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/app/dashboard' },
  { text: 'Workflows', icon: <WorkflowsIcon />, path: '/app/workflows' },
  { text: 'Executions', icon: <ExecutionsIcon />, path: '/app/executions' },
  { text: 'Chat', icon: <ChatIcon />, path: '/app/chat' },
  { text: 'Imported Nodes', icon: <CloudDownloadIcon />, path: '/app/nodes/imported' },
  { text: 'Node Editor', icon: <NodeEditorIcon />, path: '/app/node-editor' },
  { text: 'Node Library', icon: <RegistryIcon />, path: '/app/registry' },
  { text: 'Subscription', icon: <SubscriptionIcon />, path: '/app/subscription' },
];

export const Layout: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const hasPerm = (perm: string) => {
    if (!user) return false;
    if (user.role === 'system_admin') return true;
    return Array.isArray((user as any).permissions) && (user as any).permissions.includes(perm);
  };

  const canAccessAdmin = () => {
    if (!user) return false;
    if (user.role === 'system_admin') return true;
    return hasPerm('org.manage') || hasPerm('user.manage') || hasPerm('credential.view');
  };

  const visibleAdminMenuItems = adminMenuItems.filter((item) => {
    if (user?.role === 'system_admin') return true;
    const required = item.perms || [];
    return required.some((perm) => hasPerm(perm));
  });

  const visibleAppMenuItems = appMenuItems.filter((item) => {
    if (item.path.startsWith('/app/workflows')) return hasPerm('workflow.view');
    if (item.path.startsWith('/app/executions')) return hasPerm('execution.view');
    if (item.path.startsWith('/app/chat')) return hasPerm('workflow.execute');
    return true;
  });

  const renderMenuItems = (items: any[]) => {
    return items.map((item) => (
      <ListItem key={item.text} disablePadding>
        <ListItemButton
          selected={isActive(item.path)}
          onClick={() => {
            navigate(item.path);
          }}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.text} />
        </ListItemButton>
      </ListItem>
    ));
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${open ? drawerWidth : 0}px)`,
          ml: `${open ? drawerWidth : 0}px`,
          transition: 'width 0.3s',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, display: open ? 'none' : 'block' }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            ODA
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {user?.first_name} {user?.last_name}
            </Typography>
            <Tooltip title="Account settings">
              <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0 }}>
                <Avatar sx={{ width: 32, height: 32 }}>
                  {user?.first_name?.[0] || user?.email?.[0]}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: [1] }}>
          <IconButton onClick={handleDrawerClose}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>
        <Divider />

        <List>{renderMenuItems(visibleAppMenuItems)}</List>
        
        {canAccessAdmin() && (
          <>
            <Divider />
            <List>
              <ListItem>
                <ListItemText primary="Administration" sx={{ fontWeight: 'bold', color: 'text.secondary' }} />
              </ListItem>
            </List>
            <List>{renderMenuItems(visibleAdminMenuItems)}</List>
          </>
        )}

          {/* Bottom pinned account actions */}
          <Box sx={{ flexGrow: 1 }} />
          <Divider />
          <List>
            <ListItem disablePadding>
              <ListItemButton
                selected={isActive('/app/settings')}
                onClick={() => navigate('/app/settings')}
              >
                <ListItemIcon>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                onClick={async () => {
                  await handleLogout();
                  navigate('/login');
                }}
              >
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: '100vh',
          overflow: 'auto',
          width: `calc(100% - ${open ? drawerWidth : 0}px)`,
          transition: 'width 0.3s',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
      >
        {userMenuItems.map((item) => (
          <MenuItem
            key={item.text}
            onClick={() => {
              handleProfileMenuClose();
              navigate(item.path);
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            {item.text}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};